import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  Details,
  type DetailsProps,
  EmptyStation,
  type FormActionData,
  InfoPanel,
  MessageBar,
  Paragraph,
  ReadOnlyField,
  Section,
  type StationMessage,
  ToggleButtonField,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import { Field } from 'formik';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { ensureError } from '../../../../common/errors/ensure-error';
import {
  AzureStorageAccountContainerDocument,
  type AzureStorageAccountContainerQuery,
  type AzureStorageContainerQueryPayload,
  DeleteAzureStorageContainerDocument,
  type DeleteAzureStorageContainerMutation,
  UpdateAzureStorageContainerDocument,
  type UpdateAzureStorageContainerMutation,
} from '../../../../generated/hosting-service';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';
import { environmentApolloClient } from '../../../environmentApolloClient';
import { ContainerIsReadyToUse } from './ContainerDetailsMessages';

interface ContainerAudit {
  updatedAt: string | undefined;

  tenantId: string;
  environmentId: string;
}

type ContainerData = NonNullable<
  AzureStorageContainerQueryPayload['container']
>;

export const StorageContainerDetails: React.FC = () => {
  const { envId, storageAccountName, storageType, containerName } = useParams<{
    envId: string;
    action: string;
    storageAccountName: string;
    storageType: string;
    containerName: string;
  }>();

  const [containerDetails, setContainerDetails] = useState<ContainerData>();
  const [containerAudit, setContainerAudit] = useState<ContainerAudit>();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [message, setMessage] = useState<StationMessage>();
  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        // Check the status of hosting-service
        const statusMessage = await getManagedServiceAvailabilityMessage(
          ManagedServiceId.HOSTING_SERVICE,
          envId,
        );

        if (statusMessage !== null) {
          setServiceStatusMessage(statusMessage);
        } else {
          const envApolloClient = environmentApolloClient(
            envId,
            new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
          );

          const containerDetailsResult =
            await envApolloClient?.query<AzureStorageAccountContainerQuery>({
              query: AzureStorageAccountContainerDocument,
              variables: {
                input: {
                  provider: storageType,
                  storageAccountName,
                  name: containerName,
                },
              },
              fetchPolicy: 'network-only',
            });

          const containerInfo =
            containerDetailsResult.data.azureStorageContainer;
          if (containerInfo && containerInfo.container) {
            setContainerDetails(containerInfo.container);
            setContainerAudit({
              environmentId: envId,
              tenantId: containerInfo.tenantId,
              updatedAt: containerInfo.container.updatedAt,
            });
            setMessage(ContainerIsReadyToUse);
          }
        }
      } catch (e) {
        const error = ensureError(e);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [envId, storageType, storageAccountName, containerName]);

  const deleteContainer = async (
    onActionExecuted: () => void,
  ): Promise<void> => {
    const envApolloClient = environmentApolloClient(
      envId,
      new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
    );

    const deleteContainerResult =
      await envApolloClient?.mutate<DeleteAzureStorageContainerMutation>({
        mutation: DeleteAzureStorageContainerDocument,
        variables: {
          input: {
            provider: storageType,
            storageAccountName,
            name: containerName,
          },
        },
        errorPolicy: 'all',
      });

    if (!deleteContainerResult?.errors?.length) {
      onActionExecuted();
      return;
    }

    setError(deleteContainerResult.errors[0].message);
  };

  const { actions } = useActions(
    containerDetails !== undefined,
    envId,
    storageType,
    storageAccountName,
    containerName,
    deleteContainer,
  );

  const onSubmit = useCallback(
    async (
      formData: ContainerData,
      initialData: DetailsProps<ContainerData>['initialData'],
    ): Promise<void> => {
      if (initialData.loading) {
        return;
      }

      const envApolloClient = environmentApolloClient(
        envId,
        new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
      );

      const updatingResult =
        await envApolloClient?.mutate<UpdateAzureStorageContainerMutation>({
          mutation: UpdateAzureStorageContainerDocument,
          variables: {
            input: {
              provider: storageType,
              storageAccountName,
              name: containerName,
              patch: {
                hasPublicReadAccess: formData.hasPublicReadAccess,
              },
            },
          },
        });

      if (updatingResult?.data?.updateAzureStorageContainer?.error) {
        throw new Error(
          updatingResult?.data?.updateAzureStorageContainer?.error,
        );
      }
    },
    [envId, storageType, storageAccountName, containerName],
  );

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="Container Details">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <Details
          defaultTitle={containerName}
          subtitle="Container Details"
          initialData={{
            data: containerDetails,
            loading,
            error,
          }}
          saveData={onSubmit}
          infoPanel={
            containerAudit ? <Panel audit={containerAudit} /> : undefined
          }
          stationMessage={message}
          actions={actions}
        >
          {containerDetails ? <Form details={containerDetails} /> : undefined}
        </Details>
      )}
    </>
  );
};

const Form: React.FC<{ details: ContainerData | undefined }> = ({
  details,
}) => {
  return (
    <>
      <Field name="name" label="Name" as={ReadOnlyField} />

      <Field
        name="hasPublicReadAccess"
        label="Public Read Access"
        tooltipContent={
          'Data in a public container is accessible from outside using HTTPS'
        }
        as={ToggleButtonField}
      />

      <Field
        value={details?.publicUrl ? 'URL: ' + details?.publicUrl : ''}
        as={ReadOnlyField}
      />
    </>
  );
};

const Panel: React.FC<{
  audit: Partial<ContainerAudit> | undefined;
}> = ({ audit }) => {
  return (
    <InfoPanel>
      <Section>
        <Paragraph title="Last Modified">
          {audit?.updatedAt ? new Date(audit.updatedAt).toLocaleString() : ''}
        </Paragraph>
      </Section>
      <Section>
        <Paragraph title="Tenant ID">{audit?.tenantId}</Paragraph>
        <Paragraph title="Environment ID">{audit?.environmentId}</Paragraph>
      </Section>
    </InfoPanel>
  );
};

function useActions(
  containerLoaded: boolean,
  envId: string,
  provider: string,
  storageName: string,
  containerName: string,
  onContainerDelete: (onActionExecuted: () => void) => Promise<void>,
): {
  readonly actions: FormActionData<Partial<ContainerData>>[];
} {
  const history = useHistory();

  const actions: FormActionData<Partial<ContainerData>>[] = [];
  if (containerLoaded) {
    actions.push(
      {
        label: 'SAS Token',
        path: `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/storage/${provider}/${storageName}/containers/${containerName}/sas`,
      },
      {
        label: 'Delete',
        confirmationMode: 'Advanced',
        confirmationConfig: {
          body: 'Deleting a container is a destructive operation, where all of its stored content will be removed. This will invalidate any usages that refer to this container including Image/Video service profiles. Please confirm to proceed.',
        },
        onActionSelected: () => {
          const onActionExecuted = async (): Promise<void> => {
            history.push(
              `/env/${envId}/services/${ManagedServiceId.HOSTING_SERVICE}/config/storage/${provider}/${storageName}/containers`,
            );
          };
          onContainerDelete(onActionExecuted);
        },
      },
    );
  }

  return { actions } as const;
}

export const StorageContainerDetailsCrumb: BreadcrumbResolver = ({
  containerName,
}) => {
  return containerName;
};
